import React, { forwardRef } from "react";
import { cn } from "@semo/shared-ui";

/**
 * 카드 폭 단계. 카드별로 max-w-* 를 직접 쓰지 말고 반드시 이 중 하나를 고를 것.
 * 새 단계를 늘리기 전에, 그 폭을 공유하는 카드가 둘 이상인지 먼저 확인한다.
 * (한 카드만 쓰는 폭은 예전 PaymentPendingCard 의 w-[320px] 과 같은 일회성 예외가 된다.)
 */
// MessageLayout 이 본문 칼럼에 같은 상한을 씌우려고 읽는다(#835) — 두 값이 어긋나면
// 시간/읽음이 카드에서 떨어져 채팅 영역 끝으로 밀린다.
export const CARD_SIZE_CLASSES = {
  /** 요약형 — 견적, 결제, 취소, 구매확정 등 대부분의 카드 */
  default: "max-w-sm",
  /** 문서형 — 거래명세서처럼 카드 안에서 내용을 다 보여주는 기록물 */
  document: "max-w-md",
} as const;

export type MessageCardSize = keyof typeof CARD_SIZE_CLASSES;

interface MessageCardWrapperProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  /** 폭 단계 (default: 384px / document: 448px) */
  size?: MessageCardSize;
  className?: string;
}

/**
 * MessageCardWrapper - 카드형 메시지의 공통 레이아웃 래퍼
 *
 * 모든 카드형 메시지(견적, 계약, 결제 등)의 너비를 일관되게 제한합니다.
 * Card Component의 Root Element로 사용하여 표준 규격을 준수하게 합니다.
 *
 * 폭은 size prop 으로만 정합니다. className 으로 max-w-* 를 넘기면 twMerge 가
 * 아래 base 를 덮어쓰기 때문에, 단계 밖의 폭이 조용히 생겨납니다.
 *
 * 가용 폭이 상한보다 좁으면(모바일 등) 상한 대신 가용 폭을 채웁니다.
 * 단, 카드가 내용 길이에 따라 들쭉날쭉해지지 않으려면 부모 행의 폭이 확정되어
 * 있어야 하고, MessageLayout 의 card variant(w-full) 가 그 역할을 합니다.
 */
export const MessageCardWrapper = forwardRef<
  HTMLDivElement,
  MessageCardWrapperProps
>(({ children, size = "default", className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      className={cn(
        "w-full",
        CARD_SIZE_CLASSES[size],
        "flex flex-col",
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
});

MessageCardWrapper.displayName = "MessageCardWrapper";
